import { ProCard } from '@ant-design/pro-components'
import { sync } from '@app/icare/client/sync'
import { type Pharmacy, type PharmacyIdType, PharmacySchema } from '@app/icare/entities/pharmacy/schema/Pharmacy'
import { EntityDrawerShell } from '@app/icare/form/EntityDrawerShell'
import { AddressFieldGroup } from '@app/icare/form/group/AddressFieldGroup'
import { useEntityDrawer } from '@app/icare/hooks/useEntityDrawer'
import { t } from '@lingui/core/macro'
import { SectionFlow } from '@pkg/form/index'
import type { SectionDef } from '@pkg/form/useSectionFlow'
import { zodDefaultInstance } from '@pkg/shared/form'
import { createTypedModal, type EditModalProps, type EditModalResult } from '@pkg/ui/modal'
import { Flex } from 'antd'

const sections = [
  { name: 'basic', title: t`Allgemein` },
  { name: 'misc', title: t`Sonstiges` },
] as const satisfies readonly SectionDef[]

type PharmacySection = (typeof sections)[number]['name']

type PharmacyEditDrawerProps =
  | (EditModalProps<Pharmacy, PharmacySection, PharmacyIdType> & {
      showGlobalImpactWarning?: boolean
    })
  | undefined

type PharmacyEditDrawerShow = (props?: PharmacyEditDrawerProps) => Promise<EditModalResult<Pharmacy>>

export const PharmacyEditDrawer = createTypedModal<
  PharmacyEditDrawerProps,
  EditModalResult<Pharmacy>,
  PharmacyEditDrawerShow
>(props => {
  const drawer = useEntityDrawer({
    schema: PharmacySchema,
    entityId: props?.id,
    sections,
    section: props?.section,
    formDefaults: {
      ...zodDefaultInstance(PharmacySchema),
      ...props?.defaults,
    },
    onPersist: async value => {
      await sync.mutate(value)
    },
  })

  const { form } = drawer

  return (
    <EntityDrawerShell drawer={drawer} showWarnings={props?.showGlobalImpactWarning}>
      <SectionFlow.Sections
        items={[
          {
            name: 'basic',
            title: t`Allgemein`,
            children: (
              <ProCard>
                <Flex vertical gap="small">
                  <form.AppField
                    name="name"
                    children={field => (
                      <field.Text label={t`Name`} className="grow" disabled={props?.showGlobalImpactWarning} />
                    )}
                  />

                  <AddressFieldGroup form={form} fields="address" />

                  <form.AppField name="contact" children={field => <field.ContactGroup hidePreferredCommunication />} />
                </Flex>
              </ProCard>
            ),
          },
          {
            name: 'misc',
            title: t`Sonstiges`,
            children: (
              <ProCard>
                <Flex vertical gap="small">
                  <form.AppField
                    name="comment"
                    children={field => <field.TextArea label={t`Bemerkungen`} className="grow" />}
                  />
                </Flex>
              </ProCard>
            ),
          },
        ]}
      />
    </EntityDrawerShell>
  )
})
